import moment from 'moment-timezone';

/**
 * Determines whether or not the needle string is contained within the
 * haystack string in a case-insensitive fashion. If the haystack is
 * null or undefined, it will be
 *
 * @param haystack
 *  A string in which to search for the specified needle.
 * @param needle
 *  A string to determine whether or not it is found within the haystack
 *  string.
 * @returns {boolean}
 */
function contains(haystack, needle) {
  const source = (haystack || '').toString().toLowerCase();
  const query = (needle || '').toString().toLowerCase();
  return source.search(query) !== -1;
}

/**
 * Returns a camelCased value from a snaked_cased value
 * @param value
 * @returns {String}
 */
function snakeToCamel(value) {
  return value.replace(/([-_][a-z])/ig, (match) => match.toUpperCase()
    .replace('-', '')
    .replace('_', ''));
}

/**
 * Convers objects with snake_case keys to camelCase keys.
 * @param snakeCasedObject
 * @returns {void|*|number|Hash}
 */
function keysToCamelCase(snakeCasedObject) {
  return Object.entries(snakeCasedObject).reduce((previous, [key, value]) => {
    const output = previous;
    output[snakeToCamel(key)] = value;
    return output;
  }, {});
}

/**
 * Friendly-format an iso8601 string or return a default in the case of falsey input.
 * @returns {string} Formatted date. e.g.: September 30, 1900 7:02 PM
 */
function formatDatetime(dt, includeHour = true, timezone = 'utc') {
  const format = includeHour ? 'MMMM DD[,] YYYY HH:mm z' : 'MMMM DD[,] YYYY z';
  return dt ? moment(dt).tz(timezone).format(format) : '--';
}

/**
 * Friendly-format an iso8601 string with local time or return a default in the case of
 * falsey input.
 * @returns {string} Formatted date. e.g.: September 30, 1900 7:02 PM
 */
function formatDatetimeLocal(dt) {
  return dt ? moment(dt).format('[Local Time: ]MMMM DD[,] YYYY hh:mm a') : '--';
}

/**
 * Check if date1 is strictly before date 2
 * @param date1
 * @param date2
 * @returns {boolean}
 */
function isBefore(date1, date2) {
  return moment(date1).isBefore(date2);
}

/**
 * check if date 1 is strictly after date 2
 * @param date1
 * @param date2
 * @returns {boolean}
 */
function isAfter(date1, date2) {
  return moment(date1).isAfter(date2);
}

/**
 * Find the start of the month for a given date.
 * @param date
 * @return {string} ISO8601 string of the start of the month in UTC
 */
function startOfMonth(date) {
  return moment(date).startOf('month').format();
}

/**
 * Find the end of the month for a given date.
 * @param date
 * @return {string} ISO8601 string of the end of the month in UTC
 */
function endOfMonth(date) {
  return moment(date)
    .endOf('month')
    .startOf('day')
    .format();
}

/**
 * Return the timestamp normalized to a time of 00:00:00
 * @param date
 * @return {string} ISO8601 string of the normalized timestamp in specified timezone (default UTC)
 */
function startOfDay(date, timezone = 'utc') {
  return moment(date).tz(timezone)
    .startOf('day')
    .format();
}

/**
 * Return the timestamp normalized to a time of 23:59:59
 * @param date
 * @return {string} ISO8601 string of the normalized timestamp in specified timezone (default UTC)
 */
function endOfDay(date, timezone = 'utc') {
  return moment(date).tz(timezone)
    .endOf('day')
    .format();
}

/**
 * Return the ISO-8601 string representation of a date or null if the date is null
 * @param date
 */
function toIsoString(date) {
  return date ? moment(date).format() : null;
}

/**
 * Get the timezone abbreviation for a given timestamp in the specified
 * timezone. For example 'EST' or 'CDT'. Useful if the abbreviation can
 * change with daylight savings time.
 */
function zoneAbbreviation(date, timezone = 'utc') {
  return moment(date).tz(timezone).zoneAbbr();
}

export default {
  contains,
  snakeToCamel,
  keysToCamelCase,
  formatDatetime,
  formatDatetimeLocal,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  toIsoString,
  zoneAbbreviation,
};
